import React, { useState, useEffect, useRef } from 'react';
import {
  Building2, Plus, Search, Edit2, Trash2, MapPin, Globe, Users,
  Camera, Car, CreditCard, Settings, Upload, FileText, BarChart3,
  CheckCircle, XCircle, AlertTriangle, ChevronRight, Shield, Clock,
  DollarSign, Activity, X, Save, Eye
} from 'lucide-react';

const API_URL = '';

const REGIONS = [
  'South Ayrshire', 'North Ayrshire', 'East Ayrshire',
  'Glasgow', 'Edinburgh', 'Highland', 'Fife',
  'Dundee', 'Aberdeen', 'Borders', 'Dumfries & Galloway',
  'Yorkshire', 'North West', 'Midlands', 'South East', 'South West'
];

const BARRIER_MODES = [
  { value: 'standard', label: 'Standard', description: 'Barrier controlled entry/exit' },
  { value: 'freeflow', label: 'Freeflow', description: 'ANPR-only, no barriers' },
  { value: 'hybrid', label: 'Hybrid', description: 'Barriers for non-whitelisted only' }
];

export default function AdminSites() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [saving, setSaving] = useState(false);
  
  // Stats modal
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedSiteStats, setSelectedSiteStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    client: '',
    site_code: '',
    address: '',
    region: '',
    location: { latitude: null, longitude: null },
    hardware: {
      entry_barriers: [],
      exit_barriers: [],
      combo_barriers: [],
      entry_cameras: [],
      exit_cameras: [],
      combo_cameras: [],
      kiosks: [],
      payment_terminals: []
    },
    commercial: {
      tariff_id: '',
      grace_period_minutes: 15,
      free_period_minutes: 0,
      max_stay_hours: null,
      enforcement_enabled: false,
      barrier_mode: 'standard',
      freeflow_enabled: false
    },
    tariff: {
      type: 'banded',
      bands: [
        { up_to_minutes: 60, price: 0 },
        { up_to_minutes: 120, price: 2 },
        { up_to_minutes: 240, price: 4 },
        { up_to_minutes: 480, price: 6 },
        { up_to_minutes: 1440, price: 8 }
      ],
      max_daily: 8
    }
  });
  
  // Hardware input state
  const [hardwareInput, setHardwareInput] = useState({
    entry_barriers: '',
    exit_barriers: '',
    combo_barriers: '',
    entry_cameras: '',
    exit_cameras: '',
    combo_cameras: '',
    kiosks: '',
    payment_terminals: ''
  });

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSites();
  }, [showInactive]);

  const fetchSites = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sites?include_inactive=${showInactive}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setSites(data);
      }
    } catch (err) {
      console.error('Failed to fetch sites:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteStats = async (siteId) => {
    setStatsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sites/${siteId}/stats`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedSiteStats(data);
        setShowStatsModal(true);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingSite(null);
    setFormData({
      name: '',
      client: '',
      site_code: '',
      address: '',
      region: '',
      location: { latitude: null, longitude: null },
      hardware: {
        entry_barriers: [],
        exit_barriers: [],
        combo_barriers: [],
        entry_cameras: [],
        exit_cameras: [],
        combo_cameras: [],
        kiosks: [],
        payment_terminals: []
      },
      commercial: {
        tariff_id: '',
        grace_period_minutes: 15,
        free_period_minutes: 0,
        max_stay_hours: null,
        enforcement_enabled: false,
        barrier_mode: 'standard',
        freeflow_enabled: false
      },
      tariff: {
        type: 'banded',
        bands: [
          { up_to_minutes: 60, price: 0 },
          { up_to_minutes: 120, price: 2 },
          { up_to_minutes: 240, price: 4 },
          { up_to_minutes: 480, price: 6 },
          { up_to_minutes: 1440, price: 8 }
        ],
        max_daily: 8
      }
    });
    setHardwareInput({
      entry_barriers: '',
      exit_barriers: '',
      combo_barriers: '',
      entry_cameras: '',
      exit_cameras: '',
      combo_cameras: '',
      kiosks: '',
      payment_terminals: ''
    });
    setActiveTab('info');
    setShowModal(true);
  };

  const openEditModal = (site) => {
    setEditingSite(site);
    setFormData({
      name: site.name || '',
      client: site.client || '',
      site_code: site.site_code || '',
      address: site.address || '',
      region: site.region || '',
      location: site.location || { latitude: null, longitude: null },
      hardware: site.hardware || {
        entry_barriers: [],
        exit_barriers: [],
        combo_barriers: [],
        entry_cameras: [],
        exit_cameras: [],
        combo_cameras: [],
        kiosks: [],
        payment_terminals: []
      },
      commercial: site.commercial || {
        tariff_id: '',
        grace_period_minutes: 15,
        free_period_minutes: 0,
        max_stay_hours: null,
        enforcement_enabled: false,
        barrier_mode: 'standard',
        freeflow_enabled: false
      },
      tariff: site.tariff || {
        type: 'banded',
        bands: [
          { up_to_minutes: 60, price: 0 },
          { up_to_minutes: 120, price: 2 },
          { up_to_minutes: 240, price: 4 }
        ],
        max_daily: 8
      }
    });
    
    // Convert hardware arrays to comma-separated strings for input
    const hw = site.hardware || {};
    setHardwareInput({
      entry_barriers: (hw.entry_barriers || []).join(', '),
      exit_barriers: (hw.exit_barriers || []).join(', '),
      combo_barriers: (hw.combo_barriers || []).join(', '),
      entry_cameras: (hw.entry_cameras || []).join(', '),
      exit_cameras: (hw.exit_cameras || []).join(', '),
      combo_cameras: (hw.combo_cameras || []).join(', '),
      kiosks: (hw.kiosks || []).join(', '),
      payment_terminals: (hw.payment_terminals || []).join(', ')
    });
    
    setActiveTab('info');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Parse hardware inputs
      const hardware = {
        entry_barriers: hardwareInput.entry_barriers.split(',').map(s => s.trim()).filter(Boolean),
        exit_barriers: hardwareInput.exit_barriers.split(',').map(s => s.trim()).filter(Boolean),
        combo_barriers: hardwareInput.combo_barriers.split(',').map(s => s.trim()).filter(Boolean),
        entry_cameras: hardwareInput.entry_cameras.split(',').map(s => s.trim()).filter(Boolean),
        exit_cameras: hardwareInput.exit_cameras.split(',').map(s => s.trim()).filter(Boolean),
        combo_cameras: hardwareInput.combo_cameras.split(',').map(s => s.trim()).filter(Boolean),
        kiosks: hardwareInput.kiosks.split(',').map(s => s.trim()).filter(Boolean),
        payment_terminals: hardwareInput.payment_terminals.split(',').map(s => s.trim()).filter(Boolean)
      };
      
      const payload = {
        ...formData,
        hardware,
        location: formData.location.latitude ? formData.location : null
      };
      
      if (editingSite) {
        const res = await fetch(`${API_URL}/api/sites/${editingSite.site_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Update failed');
        }
      } else {
        const res = await fetch(`${API_URL}/api/sites`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Create failed');
        }
      }
      
      setShowModal(false);
      fetchSites();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sites/${deleteTarget.site_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Delete failed');
      }
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchSites();
    } catch (err) {
      alert(err.message);
    }
  };

  const addTariffBand = () => {
    const lastBand = formData.tariff.bands[formData.tariff.bands.length - 1];
    setFormData({
      ...formData,
      tariff: {
        ...formData.tariff,
        bands: [...formData.tariff.bands, {
          up_to_minutes: (lastBand?.up_to_minutes || 0) + 60,
          price: (lastBand?.price || 0) + 2
        }]
      }
    });
  };

  const removeTariffBand = (index) => {
    setFormData({
      ...formData,
      tariff: {
        ...formData.tariff,
        bands: formData.tariff.bands.filter((_, i) => i !== index)
      }
    });
  };

  const updateTariffBand = (index, field, value) => {
    const bands = [...formData.tariff.bands];
    bands[index] = { ...bands[index], [field]: Number(value) };
    setFormData({
      ...formData,
      tariff: { ...formData.tariff, bands }
    });
  };

  const filteredSites = sites.filter(site => 
    site.name?.toLowerCase().includes(search.toLowerCase()) ||
    site.site_code?.toLowerCase().includes(search.toLowerCase()) ||
    site.region?.toLowerCase().includes(search.toLowerCase()) ||
    site.client?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-sites">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-7 h-7 text-blue-600" />
            Site Management
          </h1>
          <p className="text-gray-600 mt-1">Configure and manage parking sites</p>
        </div>
        <button
          onClick={openCreateModal}
          data-testid="add-site-btn"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="w-5 h-5" />
          Add Site
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4 flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search sites..."
              data-testid="search-input"
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded"
          />
          <span className="text-sm text-gray-700">Show inactive</span>
        </label>
      </div>

      {/* Sites Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredSites.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            No sites found
          </div>
        ) : (
          filteredSites.map(site => (
            <div
              key={site.site_id}
              data-testid={`site-${site.site_id}`}
              className={`bg-white rounded-lg border p-4 ${!site.is_active ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{site.name}</h3>
                  {site.site_code && (
                    <p className="text-sm text-gray-500 font-mono">{site.site_code}</p>
                  )}
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${site.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                  {site.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                {site.client && (
                  <p className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {site.client}
                  </p>
                )}
                {site.address && (
                  <p className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {site.address}
                  </p>
                )}
                {site.region && (
                  <p className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    {site.region}
                  </p>
                )}
              </div>
              
              {/* Quick stats */}
              {site.hardware && (
                <div className="flex gap-2 flex-wrap mb-4">
                  {(site.hardware.entry_cameras?.length || site.hardware.exit_cameras?.length || site.hardware.combo_cameras?.length) > 0 && (
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs flex items-center gap-1">
                      <Camera className="w-3 h-3" />
                      {(site.hardware.entry_cameras?.length || 0) + (site.hardware.exit_cameras?.length || 0) + (site.hardware.combo_cameras?.length || 0)} cameras
                    </span>
                  )}
                  {(site.hardware.entry_barriers?.length || site.hardware.exit_barriers?.length || site.hardware.combo_barriers?.length) > 0 && (
                    <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded text-xs flex items-center gap-1">
                      <Car className="w-3 h-3" />
                      {(site.hardware.entry_barriers?.length || 0) + (site.hardware.exit_barriers?.length || 0) + (site.hardware.combo_barriers?.length || 0)} barriers
                    </span>
                  )}
                  {site.commercial?.freeflow_enabled && (
                    <span className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      Freeflow
                    </span>
                  )}
                </div>
              )}
              
              <div className="flex gap-2 pt-2 border-t">
                <button
                  onClick={() => fetchSiteStats(site.site_id)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition"
                  title="View Stats"
                >
                  <BarChart3 className="w-4 h-4" />
                  Stats
                </button>
                <button
                  onClick={() => openEditModal(site)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded transition"
                  title="Edit"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => { setDeleteTarget(site); setShowDeleteModal(true); }}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition"
                  title="Deactivate"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {editingSite ? `Edit Site: ${editingSite.name}` : 'Create New Site'}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
              </button>
            </div>
            
            {/* Tabs */}
            <div className="border-b px-6 flex gap-4">
              {['info', 'hardware', 'commercial', 'tariff'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-3 px-1 text-sm font-medium border-b-2 transition ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab === 'info' && 'Site Info'}
                  {tab === 'hardware' && 'Hardware'}
                  {tab === 'commercial' && 'Commercial'}
                  {tab === 'tariff' && 'Tariff'}
                </button>
              ))}
            </div>
            
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
              {/* Site Info Tab */}
              {activeTab === 'info' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Site Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        data-testid="form-name"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Site Code</label>
                      <input
                        type="text"
                        value={formData.site_code}
                        onChange={(e) => setFormData({...formData, site_code: e.target.value.toUpperCase()})}
                        data-testid="form-site-code"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                        placeholder="e.g., KYL-001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                      <input
                        type="text"
                        value={formData.client}
                        onChange={(e) => setFormData({...formData, client: e.target.value})}
                        data-testid="form-client"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                      <select
                        value={formData.region}
                        onChange={(e) => setFormData({...formData, region: e.target.value})}
                        data-testid="form-region"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select region...</option>
                        {REGIONS.map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      data-testid="form-address"
                      rows={2}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                      <input
                        type="number"
                        step="any"
                        value={formData.location?.latitude || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          location: { ...formData.location, latitude: e.target.value ? parseFloat(e.target.value) : null }
                        })}
                        data-testid="form-latitude"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 55.4587"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                      <input
                        type="number"
                        step="any"
                        value={formData.location?.longitude || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          location: { ...formData.location, longitude: e.target.value ? parseFloat(e.target.value) : null }
                        })}
                        data-testid="form-longitude"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., -4.6289"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Hardware Tab */}
              {activeTab === 'hardware' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Enter hardware IDs separated by commas. Hardware can only be assigned to one site.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Entry Barriers</label>
                      <input
                        type="text"
                        value={hardwareInput.entry_barriers}
                        onChange={(e) => setHardwareInput({...hardwareInput, entry_barriers: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="e.g., BAR-001, BAR-002"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Exit Barriers</label>
                      <input
                        type="text"
                        value={hardwareInput.exit_barriers}
                        onChange={(e) => setHardwareInput({...hardwareInput, exit_barriers: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="e.g., BAR-003"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Combo Barriers</label>
                      <input
                        type="text"
                        value={hardwareInput.combo_barriers}
                        onChange={(e) => setHardwareInput({...hardwareInput, combo_barriers: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="e.g., BAR-COMBO-001"
                      />
                    </div>
                    <div className="col-span-2 border-t pt-4 mt-2"></div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Entry Cameras</label>
                      <input
                        type="text"
                        value={hardwareInput.entry_cameras}
                        onChange={(e) => setHardwareInput({...hardwareInput, entry_cameras: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="e.g., CAM-ENTRY-001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Exit Cameras</label>
                      <input
                        type="text"
                        value={hardwareInput.exit_cameras}
                        onChange={(e) => setHardwareInput({...hardwareInput, exit_cameras: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="e.g., CAM-EXIT-001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Combo Cameras</label>
                      <input
                        type="text"
                        value={hardwareInput.combo_cameras}
                        onChange={(e) => setHardwareInput({...hardwareInput, combo_cameras: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="e.g., CAM-COMBO-001"
                      />
                    </div>
                    <div className="col-span-2 border-t pt-4 mt-2"></div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Kiosks</label>
                      <input
                        type="text"
                        value={hardwareInput.kiosks}
                        onChange={(e) => setHardwareInput({...hardwareInput, kiosks: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="e.g., KIOSK-001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terminals</label>
                      <input
                        type="text"
                        value={hardwareInput.payment_terminals}
                        onChange={(e) => setHardwareInput({...hardwareInput, payment_terminals: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="e.g., TERM-001"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Commercial Tab */}
              {activeTab === 'commercial' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Grace Period (minutes)</label>
                      <input
                        type="number"
                        value={formData.commercial.grace_period_minutes}
                        onChange={(e) => setFormData({
                          ...formData,
                          commercial: {...formData.commercial, grace_period_minutes: parseInt(e.target.value) || 0}
                        })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Time after exit before payment required</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Free Period (minutes)</label>
                      <input
                        type="number"
                        value={formData.commercial.free_period_minutes}
                        onChange={(e) => setFormData({
                          ...formData,
                          commercial: {...formData.commercial, free_period_minutes: parseInt(e.target.value) || 0}
                        })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Free parking period from entry</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Stay (hours)</label>
                      <input
                        type="number"
                        value={formData.commercial.max_stay_hours || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          commercial: {...formData.commercial, max_stay_hours: e.target.value ? parseInt(e.target.value) : null}
                        })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="No limit"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Barrier Mode</label>
                      <select
                        value={formData.commercial.barrier_mode}
                        onChange={(e) => setFormData({
                          ...formData,
                          commercial: {...formData.commercial, barrier_mode: e.target.value}
                        })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        {BARRIER_MODES.map(mode => (
                          <option key={mode.value} value={mode.value}>{mode.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-6 pt-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.commercial.enforcement_enabled}
                        onChange={(e) => setFormData({
                          ...formData,
                          commercial: {...formData.commercial, enforcement_enabled: e.target.checked}
                        })}
                        className="rounded"
                      />
                      <span className="text-sm">Enforcement Enabled</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.commercial.freeflow_enabled}
                        onChange={(e) => setFormData({
                          ...formData,
                          commercial: {...formData.commercial, freeflow_enabled: e.target.checked}
                        })}
                        className="rounded"
                      />
                      <span className="text-sm">Freeflow Enabled</span>
                    </label>
                  </div>
                </div>
              )}
              
              {/* Tariff Tab */}
              {activeTab === 'tariff' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-medium text-gray-900">Tariff Bands</h3>
                      <p className="text-sm text-gray-500">Configure pricing bands for this site</p>
                    </div>
                    <button
                      type="button"
                      onClick={addTariffBand}
                      className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Plus className="w-4 h-4" />
                      Add Band
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {formData.tariff.bands.map((band, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <label className="text-xs text-gray-500">Up to (minutes)</label>
                          <input
                            type="number"
                            value={band.up_to_minutes}
                            onChange={(e) => updateTariffBand(index, 'up_to_minutes', e.target.value)}
                            className="w-full px-2 py-1 border rounded text-sm"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs text-gray-500">Price (£)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={band.price}
                            onChange={(e) => updateTariffBand(index, 'price', e.target.value)}
                            className="w-full px-2 py-1 border rounded text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeTariffBand(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="pt-4 border-t">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Daily Rate (£)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.tariff.max_daily || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        tariff: {...formData.tariff, max_daily: e.target.value ? parseFloat(e.target.value) : null}
                      })}
                      className="w-32 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
            </form>
            
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                data-testid="form-submit"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : (editingSite ? 'Save Changes' : 'Create Site')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {showStatsModal && selectedSiteStats && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                {selectedSiteStats.name} - Live Stats
              </h2>
              <button onClick={() => setShowStatsModal(false)}>
                <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <Activity className="w-4 h-4" />
                  <span className="text-sm">Active Sessions</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">{selectedSiteStats.active_sessions}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <Car className="w-4 h-4" />
                  <span className="text-sm">Today's Sessions</span>
                </div>
                <p className="text-2xl font-bold text-green-700">{selectedSiteStats.total_sessions_today}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-amber-600 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm">Revenue Today</span>
                </div>
                <p className="text-2xl font-bold text-amber-700">£{selectedSiteStats.revenue_today.toFixed(2)}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-purple-600 mb-1">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm">Whitelist Count</span>
                </div>
                <p className="text-2xl font-bold text-purple-700">{selectedSiteStats.whitelist_count}</p>
              </div>
            </div>
            {selectedSiteStats.hardware_status && Object.keys(selectedSiteStats.hardware_status).length > 0 && (
              <div className="px-6 pb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Hardware</h3>
                <div className="flex gap-2 flex-wrap">
                  {selectedSiteStats.hardware_status.barriers > 0 && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">
                      {selectedSiteStats.hardware_status.barriers} barriers
                    </span>
                  )}
                  {selectedSiteStats.hardware_status.cameras > 0 && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                      {selectedSiteStats.hardware_status.cameras} cameras
                    </span>
                  )}
                  {selectedSiteStats.hardware_status.kiosks > 0 && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                      {selectedSiteStats.hardware_status.kiosks} kiosks
                    </span>
                  )}
                  {selectedSiteStats.hardware_status.terminals > 0 && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                      {selectedSiteStats.hardware_status.terminals} terminals
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 text-red-600 mb-4">
                <AlertTriangle className="w-6 h-6" />
                <h2 className="text-lg font-semibold">Deactivate Site</h2>
              </div>
              <p className="text-gray-600">
                Are you sure you want to deactivate <strong>{deleteTarget.name}</strong>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This will hide the site from active lists but preserve all historical data.
              </p>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                data-testid="confirm-delete-btn"
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
